import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Upload, FileText, Image, Trash2, Eye, Loader2, X, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";

const MAX_SIZE_MB = 5;

const HealthRecordsPage = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const bn = language === "bn";
  const navigate = useNavigate();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [records, setRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [reportName, setReportName] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewRecord, setPreviewRecord] = useState<any | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    loadRecords();
  }, [user]);

  const loadRecords = async () => {
    if (!user) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("health_records")
      .select("id, report_name, file_type, file_data, created_at")
      .eq("patient_id", user.id)
      .order("created_at", { ascending: false });
    if (error) {
      toast({ title: bn ? "রেকর্ড লোড করা যায়নি" : "Could not load records", description: error.message, variant: "destructive" });
      setRecords([]);
    } else {
      setRecords(data ?? []);
    }
    setLoading(false);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      toast({ title: bn ? "ফাইল অনেক বড়" : "File too large", description: `Max ${MAX_SIZE_MB}MB`, variant: "destructive" });
      return;
    }
    setSelectedFile(file);
    if (!reportName) {
      setReportName(file.name.replace(/\.[^/.]+$/, ""));
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !reportName.trim() || !user) return;
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result as string;
        const fileType = selectedFile.type.startsWith("image/") ? "image" : "pdf";
        const { error } = await supabase.from("health_records").insert({
          patient_id: user.id,
          report_name: reportName.trim(),
          file_data: base64,
          file_type: fileType,
        });
        if (error) {
          toast({ title: bn ? "আপলোড ব্যর্থ" : "Upload failed", description: error.message, variant: "destructive" });
        } else {
          toast({ title: bn ? "রিপোর্ট আপলোড হয়েছে" : "Report uploaded", description: reportName.trim() });
          setReportName("");
          setSelectedFile(null);
          if (fileInputRef.current) fileInputRef.current.value = "";
          await loadRecords();
        }
        setUploading(false);
      };
      reader.readAsDataURL(selectedFile);
    } catch {
      toast({ title: bn ? "আপলোড ব্যর্থ" : "Upload failed", variant: "destructive" });
      setUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    const { error } = await supabase.from("health_records").delete().eq("id", id);
    if (error) {
      toast({ title: bn ? "মুছে ফেলা যায়নি" : "Could not delete record", description: error.message, variant: "destructive" });
      setDeletingId(null);
      return;
    }
    setRecords((prev) => prev.filter((r) => r.id !== id));
    setDeletingId(null);
  };

  const handleDownloadPDF = (record: any) => {
    const link = document.createElement("a");
    link.href = record.file_data;
    link.download = `${record.report_name}.pdf`;
    link.click();
  };

  return (
    <div className="min-h-screen bg-[#f7faf8] pb-28 text-[#245853]">
      <header className="relative overflow-hidden rounded-b-[30px] bg-[#edf7f3] px-5 pb-8 pt-7">
        <div className="relative z-10 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-[#dff1eb] text-[#347f78]"
            aria-label={bn ? "ফিরে যান" : "Go back"}
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-0.5 rounded-full bg-[#e1f2ed] p-1 text-[11px] font-bold">
            <span className="rounded-full bg-[#2b8b82] px-3 py-1.5 text-white">EN</span>
            <span className="px-2 text-[#3e716c]">বাংলা</span>
          </div>
        </div>

        <div className="relative z-10 mt-2 max-w-[58%] pl-14">
          <h1 className="font-serif text-[25px] font-extrabold leading-tight text-[#20534f]">
            {bn ? "স্বাস্থ্য রেকর্ড" : "Health Records"}
          </h1>
          <p className="mt-1 text-sm text-[#6d7978]">
            {bn ? "আপনার মেডিকেল রিপোর্ট সংরক্ষণ করুন" : "Store your medical test reports"}
          </p>
        </div>

        <img
          src="/health-records-hero.png"
          alt=""
          className="absolute -bottom-1 right-1 h-[155px] w-[185px] object-contain"
        />
      </header>

      <div className="relative z-10 mx-5 -mt-1">
        <div className="soft-surface space-y-3 rounded-[26px] border border-[#e2ebe7] p-5">
          <p className="flex items-center gap-2 text-sm font-extrabold uppercase tracking-wide text-[#245853]">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#e1f2ed]">
              <Upload className="h-4 w-4 text-[#398b82]" />
            </span>
            {bn ? "নতুন রিপোর্ট আপলোড করুন" : "Upload New Report"}
          </p>

          <div>
            <label className="mb-1.5 block text-xs font-bold text-[#536462]">{bn ? "রিপোর্টের নাম *" : "Report Name *"}</label>
            <input
              type="text"
              value={reportName}
              onChange={(e) => setReportName(e.target.value)}
              placeholder={bn ? "যেমন: রক্ত পরীক্ষা, এক্স-রে..." : "e.g., Blood Test, X-Ray..."}
              className="w-full rounded-full border border-[#dbe9e4] bg-[#f4faf7] px-4 py-3 text-sm font-medium text-foreground outline-none placeholder:text-[#7b8584] focus:border-[#5ba79d] focus:ring-0"
            />
          </div>

          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf"
              className="hidden"
              onChange={handleFileSelect}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex min-h-[145px] w-full flex-col items-center justify-center gap-2 rounded-[22px] border-2 border-dashed border-[#c8dfd8] bg-[#fbfdfc] px-4 py-5 transition-colors hover:border-[#63a99f] hover:bg-[#f2faf7]"
            >
              {selectedFile ? (
                <>
                  {selectedFile.type.startsWith("image/") ? (
                    <Image className="h-7 w-7 text-[#398b82]" />
                  ) : (
                    <FileText className="h-7 w-7 text-[#398b82]" />
                  )}
                  <p className="max-w-full truncate text-xs font-bold text-[#398b82]">{selectedFile.name}</p>
                  <p className="text-[10px] text-[#788684]">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                </>
              ) : (
                <>
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#e1f2ed]">
                    <Plus className="h-6 w-6 text-[#398b82]" />
                  </span>
                  <p className="text-sm font-extrabold text-[#245853]">{bn ? "ছবি বা PDF বেছে নিন" : "Choose Photo or PDF"}</p>
                  <p className="text-xs text-[#788684]">Max {MAX_SIZE_MB}MB</p>
                </>
              )}
            </button>
          </div>

          <motion.button
            onClick={handleUpload}
            disabled={!selectedFile || !reportName.trim() || uploading}
            whileTap={{ scale: 0.97 }}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-[#dcefeb] py-3.5 text-sm font-extrabold text-[#245853] transition-colors disabled:opacity-50"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploading ? (bn ? "আপলোড হচ্ছে..." : "Uploading...") : (bn ? "আপলোড করুন" : "Upload Report")}
          </motion.button>
        </div>
      </div>

      <div className="mx-5 mt-6">
        <h2 className="mb-3 font-serif text-xl font-extrabold text-[#245853]">{bn ? "আমার রিপোর্টসমূহ" : "My Reports"}</h2>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : records.length === 0 ? (
          <div className="soft-surface rounded-[26px] border border-[#e2ebe7] px-5 py-6 text-center">
            <img src="/health-records-empty.png" alt="" className="mx-auto mb-2 h-24 w-36 object-contain" />
            <p className="text-sm font-extrabold text-[#536462]">{bn ? "কোনো রিপোর্ট নেই" : "No reports yet"}</p>
            <p className="mt-1 text-xs text-[#788684]">{bn ? "উপরে আপলোড করুন" : "Upload your first report above."}</p>
          </div>
        ) : (
          <motion.div className="space-y-3" initial="hidden" animate="show" variants={{ show: { transition: { staggerChildren: 0.06 } } }}>
            {records.map((record) => (
              <motion.div
                key={record.id}
                variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } }}
                className="soft-surface flex items-center gap-3 rounded-2xl border border-[#e2ebe7] p-3"
              >
                {record.file_type === "image" ? (
                  <button onClick={() => setPreviewRecord(record)} className="shrink-0">
                    <img
                      src={record.file_data}
                      alt={record.report_name}
                      className="h-14 w-14 rounded-xl object-cover border border-border/40"
                    />
                  </button>
                ) : (
                  <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <FileText className="h-7 w-7 text-primary" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold truncate">{record.report_name}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {new Date(record.created_at).toLocaleDateString(bn ? "bn-BD" : "en-US", { day: "numeric", month: "short", year: "numeric" })}
                  </p>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full mt-1 inline-block ${record.file_type === "image" ? "bg-accent/20 text-accent-foreground" : "bg-primary/10 text-primary"}`}>
                    {record.file_type === "image" ? (bn ? "ছবি" : "Image") : "PDF"}
                  </span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {record.file_type === "image" ? (
                    <button onClick={() => setPreviewRecord(record)} className="p-2 rounded-xl hover:bg-primary/10 transition-colors">
                      <Eye className="h-4 w-4 text-primary" />
                    </button>
                  ) : (
                    <button onClick={() => handleDownloadPDF(record)} className="p-2 rounded-xl hover:bg-primary/10 transition-colors">
                      <Eye className="h-4 w-4 text-primary" />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(record.id)}
                    disabled={deletingId === record.id}
                    className="p-2 rounded-xl hover:bg-destructive/10 transition-colors"
                  >
                    {deletingId === record.id ? <Loader2 className="h-4 w-4 animate-spin text-destructive" /> : <Trash2 className="h-4 w-4 text-destructive" />}
                  </button>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>

      <AnimatePresence>
        {previewRecord && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setPreviewRecord(null)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="relative max-w-full max-h-full"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setPreviewRecord(null)}
                className="absolute -top-3 -right-3 h-8 w-8 rounded-full bg-background flex items-center justify-center shadow-lg z-10"
              >
                <X className="h-4 w-4" />
              </button>
              <img
                src={previewRecord.file_data}
                alt={previewRecord.report_name}
                className="max-h-[80vh] max-w-[90vw] rounded-2xl object-contain"
              />
              <p className="text-center text-white text-sm font-bold mt-3">{previewRecord.report_name}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HealthRecordsPage;
